<template>
    <div :class="['page', 'page--light', 'page--hide-top-nav-on-mob', {'poll--partner-app': isPartnerApp}]">
        <back-button :customCallback="routeToPollsList"
            class="back-btn"
        />

        <div v-if="!surveyFetched"
            class="skeleton"
        >
            <div class="banner">
                <div class="banner__logo" />
                <div class="banner__tagline" />
            </div>

            <div class="gradient-background">
                <div class="wrapper content-wrapper">
                    <div class="content">
                        <div class="labels">
                            <div class="labels__date" />
                        </div>

                        <div class="content__question" />

                        <div class="content__answers">
                            <div v-for="item in [1, 2, 3]"
                                :key="item"
                                class="answer"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else>
            <div class="banner">
                <partner-logo v-if="!poll.runBy"
                    :url="partner.partnerLogo"
                />

                <div class="banner__tagline">
                    {{ bannerTagline.name }} {{ bannerTagline.text }}
                </div>

                <div :style="{ backgroundImage: bannerUrl }"
                    class="banner__background"
                />

                <loading-spinner v-if="partner.partnerBanner"
                    class="banner__loader"
                />
            </div>

            <div class="gradient-background">
                <div class="wrapper content-wrapper">
                    <div class="content">
                        <poll-labels :poll="poll"
                            :showText="true"
                        />

                        <div class="content__question">{{ question }}</div>

                        <div v-if="!hasBalanceToVote"
                            class="content__tokens-needed"
                        >
                            <i class="content__tokens-needed-icon icon-token-lock" />

                            <span class="content__tokens-needed-title">
                                {{ $t('fanTokenNeededError.title', { name: token.shortCode }) }}.
                            </span>

                            <a v-if="buyTokensLink"
                                class="content__tokens-needed-link"
                                @click="goToBuyTokens()"
                            >
                                {{ $t('fanTokenNeededError.needTobuyTokens', { name: token.shortCode }) }}
                            </a>
                        </div>

                        <div v-if="isNotYetOpened"
                            class="content__not-open"
                        >
                            <i class="icon-token-lock" />
                            <span> {{ $t('pollDetails.notYetOpen') }}</span>
                        </div>

                        <div class="content__answers">
                            <poll-answer v-for="(answer, index) in answersShuffled"
                                :key="index"
                                :index="index"
                                :answer.sync="answer"
                                :hasBalanceToVote="hasBalanceToVote"
                                :hasVoted="hasVoted"
                                :poll="poll"
                                @updateModalImages="setModalImages"
                            />
                        </div>
                    </div>
                </div>

                <div class="wrapper">
                    <section v-if="isAboutPollVisible"
                        class="row"
                    >
                        <header class="row__header">
                            <h1>{{ $t('pollDetails.aboutPoll') }}</h1>
                        </header>

                        <div class="well">
                            <poll-labels :poll="poll"
                                :showText="true"
                                :showIsBoundOnly="true"
                            />

                            <div>{{ aboutPoll }}</div>

                            <a v-if="poll.informationUrl"
                                :href="poll.informationUrl"
                                target="_blank"
                            >
                                {{ poll.informationUrl }}
                            </a>
                        </div>
                    </section>

                    <section v-if="isVoteDistributionVisible"
                        class="row vote-distribution"
                    >
                        <header class="row__header">
                            <h1>{{ $t('pollDetails.voteDistribution') }}</h1>

                            <a v-if="showBlockchainAndQuorum"
                                class="row__header-link row__header-link--margined"
                                :href="`${blockchainExplorerUrl}/address/${poll.blockchainAddress}/transactions`"
                                target="_blank"
                            >
                                {{ $t('pollDetails.sociosBlockchainExplorer') }}
                            </a>
                        </header>

                        <poll-vote-distribution :poll="poll"
                            :showQuorum="showBlockchainAndQuorum"
                        />
                    </section>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    mapGetters,
    mapActions,
} from 'vuex';

// Components
import backButton from '@webapp/components/back-button/back-button.vue';
import loadingSpinner from '@webapp/components/loading-spinner/loading-spinner.vue';
import partnerLogo from '@webapp/components/partner-logo/partner-logo.vue';
import pollAnswer from '@webapp/components/poll/poll-answer.vue';
import pollLabels from '@webapp/components/poll/poll-labels.vue';
import pollVoteDistribution from '@webapp/components/poll/poll-vote-distribution.vue';

// Socios variable
import socios from '@webapp/socios.json';

// Local Types
import localEpicTypes from '@webapp/epics/types.js';
import localStoreTypes from '@webapp/store/types.js';

// Shared Modules
import sharedEpicTypes from '@shared/epics/types.js';

// Helpers
import {
    getBuyTokenLink,
} from '@webapp/helpers/partner.js';
import {
    shuffleArr,
} from '@shared/utils/general.js';

export default {
    name: 'poll',
    components: {
        backButton,
        loadingSpinner,
        partnerLogo,
        pollAnswer,
        pollLabels,
        pollVoteDistribution,
    },
    beforeRouteEnter(_to, { path: fromPath }, next) {
        next((vm) => {
            // eslint-disable-next-line no-useless-escape
            const urlRegex = new RegExp('(\/partner)\/[A-z0-9-]+(\/polls)');

            if (urlRegex.test(fromPath)) {
                vm.navigateToPartnerList = true;
            }
        });
    },
    data() {
        return {
            blockchainExplorerUrl: socios.poaBlockchainExplorer,
            buyTokensLink: null,
            surveyFetched: false,
            navigateToPartnerList: false,
            shuffledIds: [],
        };
    },
    computed: {
        ...mapGetters([
            'voteTokenOfferingsByStatusAndTokenId',
            'getTranslatedStr',
            'currentSurvey',
            'getLatestSimpleValidationError',
            'isPartnerApp',
        ]),

        ...mapGetters('voteTokens', ['getVoteTokenByCurrencyId']),

        ...mapGetters('partners', ['partnerById']),

        ...mapGetters('wallet', ['decentralizedLedgerByToken']),

        ...mapGetters('marketplace', ['availableMarkets']),

        ...mapGetters('voteTokens', ['isTokenPurchaseRestricted']),

        aboutPoll() {
            return this.getTranslatedStr(this.poll, 'information', this.poll.information) || '--';
        },

        bannerTagline() {
            return {
                name: this.poll.runBy || this.partner.name,
                text: this.poll.introductionText || this.$t('pollDetails.needYourHelp') + '!',
            };
        },

        bannerUrl() {
            let image = null;

            if (this.poll.images && this.poll.images.length > 0) {
                image = this.poll.images.find((image) => image.imageType.type === 'survey_banner_desktop');
            }

            return `url("${image && image.url ? image.url : this.partner.partnerBanner}")`;
        },

        hasBalanceToVote() {
            return this.poll.currencyId ? this.ledgerBalance > 0 : true;
        },

        hasVoted() {
            return this.poll.userVote;
        },
        isAboutPollVisible() {
            return this.poll.isBound || this.poll.information || this.poll.informationUrl;
        },

        isNotYetOpened() {
            return this.poll.status === 'scheduled';
        },

        isOpenedOrScheduled() {
            return ['scheduled', 'opened'].includes(this.poll.status);
        },

        isVoteDistributionVisible() {
            if (this.poll.type === 'integrity' && this.poll.status.includes('closed')) {
                return this.poll.disclosedAt
                    ? new Date(this.poll.disclosedAt).getTime() < new Date().getTime()
                    : true;
            }

            if (this.poll.type === 'integrity') {
                return false;
            }

            return true;
        },

        ledgerBalance() {
            const {
                balance = 0,
                lockedBalance = 0,
            } = this.decentralizedLedgerByToken(this.token.currencyId);

            return (Number(balance) + Number(lockedBalance) || 0);
        },

        partner() {
            return this.partnerById(this.poll.partnerId);
        },

        partnerMarket() {
            return this.availableMarkets.find(({ partnerId }) => this.poll.partnerId === partnerId) ?? {};
        },

        poll() {
            return this.$route.params.id && this.currentSurvey ? this.currentSurvey : {};
        },

        answersShuffled() {
            const answers = this.shuffledIds.map((id) => this.poll.answers.find((answer) => answer.id === id));

            return this.poll.answers ? answers : [];
        },

        question() {
            const question = (this.getTranslatedStr(this.poll, 'question', this.poll.question) || '').trim();

            document.title = `${question} | Poll | Socios.com`;

            return question;
        },

        token() {
            return this.getVoteTokenByCurrencyId(this.poll.currencyId);
        },

        voteTokenOfferings() {
            return this.voteTokenOfferingsByStatusAndTokenId(['opened', 'published'], this.token.id);
        },

        isBuyingRestricted() {
            return this.isTokenPurchaseRestricted(this.partnerMarket.baseCurrency);
        },

        routeToPollsList() {
            return this.navigateToPartnerList
                ? {
                    path: `/partner/${this.poll.partnerId}/polls`,
                    query: {
                        listToggle: this.isOpenedOrScheduled ? 'current' : 'finished',
                    },
                }
                : null;
        },

        showBlockchainAndQuorum() {
            return !this.isPartnerApp && Boolean(this.poll.currencyId);
        },
    },
    watch: {
        availableMarkets(newAvailableMarkets) {
            if (!this.isBuyingRestricted) {
                this.setBuyTokensLink(newAvailableMarkets);
            }
        },
        getLatestSimpleValidationError: 'onValidationError',
    },
    async mounted() {
        await this.loadMarkets();

        if (!this.wallet || !this.wallet.id) {
            this.getWallet({
                chilizCurrencyId: socios.chilizCurrencyId,
            });
        }

        await this.fetchSurvey(this.$route.params.id);

        if (this.poll && this.shuffledIds && !this.shuffledIds.length) {
            this.shuffledIds = shuffleArr(this.poll.answers.map(({ id }) => id));
        }
        this.surveyFetched = true;
    },
    methods: {
        ...mapActions({
            setModal: localStoreTypes.SET_MODAL,
            loadMarkets: localEpicTypes.GET_AVAILABLE_MARKETS,
            fetchSurvey: 'fetchSingleSurvey',
            getWallet: sharedEpicTypes.GET_WALLET,
        }),

        setModalImages(answerImages) {
            this.setModal({
                isVisible: true,
                componentToLoad: 'components/poll/poll-multi-images',
                data: {
                    images: answerImages,
                },
            });
        },

        setBuyTokensLink(market) {
            this.buyTokensLink = getBuyTokenLink({
                partner: this.partner,
                voteTokenOfferings: this.voteTokenOfferings,
                markets: market,
            });
        },

        goToBuyTokens() {
            this.$router.push(this.buyTokensLink);
        },

        onValidationError(error) {
            const fetchErrors = this.utilGlobalHandleErrorMessages(error);

            this.$notify({
                type: 'error',
                text: fetchErrors.message,
            });

            this.$router.push('/polls');
        },
    },
};
</script>

<style lang="scss" scoped>
.page {
    background-color: $athens-gray-color;

    .gradient-background {
        padding-bottom: 8rem;
    }

    @include max-width(md) {
        &.poll--partner-app {
            padding-top: 70px;
        }

        .back-btn {
            top: 10rem;
        }
    }
}

.banner {
    min-height: 32rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    position: relative;

    &__background {
        position: absolute;
        background-size: cover;
        width: 100%;
        height: 100%;
        z-index: 1;
    }

    &__loader {
        background: $dusty-gray-color;
        height: 100%;
        width: 100%;
        position: absolute;
        top: -0.25rem;
    }

    &__tagline {
        margin: 2.5rem 0 5rem;
        color: $white-color;
        font-size: 3rem;
        font-weight: bold;
        z-index: 2;
        text-align: center;
    }
}

.partner-logo {
    width: 12rem;
    height: 12rem;
    align-self: unset;
    z-index: 2;
}

.content-wrapper {
    position: relative;
    z-index: 1;
    margin-top: -4rem !important;

    .content {
        box-shadow: 0 1rem 1rem rgba($black-color, 0.08);
        background: $white-color;
        color: $cyprus-color;
        border-radius: 1rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        margin: 0 2rem 6rem;

        .labels {
            justify-content: center;
            padding: 3rem 0 2rem;
        }

        &__question {
            font-size: 1.8rem;
            font-weight: bold;
            text-align: center;
            padding: 0 2rem 2rem;

            @include min-width(lg) {
                font-size: 2.5rem;
                padding: 0 12rem 2rem;
            }
        }

        &__tokens-needed {
            font-size: 1.1rem;
            border: 0.1rem solid rgba($dusty-gray-color, 0.2);
            padding: 0.5rem 2rem;
            border-radius: 2rem;
            display: flex;
            align-items: center;
            margin: 0 1rem;

            &-icon {
                margin-right: 1.5rem;
                font-size: 1.3rem;
            }

            &-link {
                margin-left: 0.5rem;
                color: $casablanca-color;
                font-weight: bold;

                &:hover {
                    cursor: pointer;
                    opacity: 0.8;
                }
            }
        }

        &__not-open {
            margin-top: 2rem;
        }

        &__answers {
            margin: 2rem 3rem;
            width: calc(100% - 6rem);
        }
    }
}

.vote-distribution {
    margin-top: 4rem;
}

.skeleton {
    .banner {
        height: 32rem;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        position: relative;
        background: $skeleton-gradient;

        &__tagline {
            margin: 2.5rem 0 5rem;
            z-index: 1;
            background: $skeleton-gradient;
        }

        &__logo {
            width: 12rem;
            height: 12rem;
            align-self: unset;
            border-radius: 100%;
            z-index: 1;
            background: $skeleton-gradient;
        }
    }

    .content {
        .labels {
            width: 100%;
            margin-bottom: 2rem;
            font-size: 1rem;
            display: flex;
            flex-direction: column;
            align-items: center;

            &__date {
                width: 15rem;
                background: $skeleton-gradient;
                margin-bottom: 0.5rem;
                border-radius: 0.4rem;
                min-height: 2.25rem;
            }
        }

        &__question {
            background: $skeleton-gradient;
            height: 1.8rem;
        }
    }

    .answer {
        display: flex;
        background: $skeleton-gradient;
        padding: 2rem 5%;
        margin: 2rem 0;
        border-radius: 1rem;
        font-size: 1.4rem;
        align-items: center;
        position: relative;
        min-height: 8rem;

        @include min-width(md) {
            padding: 2rem 10%;
        }

        @include min-width(lg) {
            padding: 2rem 20%;
            font-size: 1.8rem;
            min-height: 12rem;
        }
    }
}
</style>